import { NavLink, useNavigate } from 'react-router-dom'
import { useUser, useClerk } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard,
  GraduationCap,
  Users,
  FileText,
  User,
  Trophy,
  Bot,
  Settings,
  ChevronDown,
  LogOut,
  Check,
  CreditCard,
} from 'lucide-react'
import { useState } from 'react'

import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { useWorkspace } from '@/hooks/useWorkspace'
import type { ApiResponse } from '@/types/api'
import type { BillingStatus } from '@/types/models'
import NotificationBell from '@/components/notifications/NotificationBell'

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Renders the subscription tier badge in the sidebar.
 * Colors follow the GradNomad brand spec:
 *   FREE       → grey (muted)
 *   PRO        → brand-purple
 *   MAX  → brand-green
 */
function TierBadge({ tier }: { tier: string }) {
  const classes: Record<string, string> = {
    FREE: 'bg-muted text-muted-foreground',
    PRO: 'bg-brand-purple/10 text-brand-purple border border-brand-purple/20',
    MAX: 'bg-brand-green/10 text-brand-green border border-brand-green/20',
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        classes[tier] ?? classes.FREE,
      )}
    >
      {tier}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Nav link definitions
// ---------------------------------------------------------------------------

const NAV_LINKS = [
  { to: '/applications', label: 'Applications', Icon: LayoutDashboard },
  { to: '/programs', label: 'Programs', Icon: GraduationCap },
  { to: '/recommenders', label: 'Recommenders', Icon: Users },
  { to: '/documents', label: 'Documents', Icon: FileText },
  { to: '/profile', label: 'Profile', Icon: User },
  { to: '/post-admit', label: 'Post-Admit', Icon: Trophy },
  { to: '/counselor', label: 'AI Counselor', Icon: Bot },
] as const

// ---------------------------------------------------------------------------
// Main Sidebar
// ---------------------------------------------------------------------------

/**
 * Sidebar — persistent left navigation for authenticated users.
 *
 * Contains:
 * - GradNomad logo
 * - Workspace switcher dropdown
 * - Primary nav links with active-state highlighting
 * - Notification bell (real, via NotificationBell component)
 * - Subscription tier badge (from billing/status API)
 * - Settings and Billing links
 * - User avatar + sign-out
 */
export default function Sidebar() {
  const { user } = useUser()
  const { signOut } = useClerk()
  const navigate = useNavigate()
  const [workspaceSwitcherOpen, setWorkspaceSwitcherOpen] = useState(false)

  const { currentWorkspace, workspaces, switchWorkspace } = useWorkspace()

  // Fetch real billing status for the tier badge
  const { data: billingData } = useQuery<ApiResponse<BillingStatus>>({
    queryKey: ['billing-status', currentWorkspace?.id],
    queryFn: () => api.get<ApiResponse<BillingStatus>>('/v1/billing/status'),
    enabled: !!currentWorkspace?.id,
    staleTime: 60_000,
  })

  const tier = billingData?.data?.tier ?? currentWorkspace?.subscriptionTier ?? 'FREE'

  async function handleSignOut() {
    await signOut()
    navigate('/')
  }

  return (
    <aside className="flex h-full w-60 flex-shrink-0 flex-col border-r bg-card">
      {/* Logo */}
      <div className="flex h-14 items-center gap-2 border-b px-4">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-blue">
          <GraduationCap className="h-4 w-4 text-white" />
        </div>
        <span className="text-base font-bold tracking-tight text-foreground">GradNomad</span>
      </div>

      {/* Workspace switcher */}
      <div className="border-b px-3 py-2">
        <div className="relative">
          <button
            type="button"
            onClick={() => setWorkspaceSwitcherOpen((o) => !o)}
            className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm font-medium text-foreground hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-haspopup="listbox"
            aria-expanded={workspaceSwitcherOpen}
          >
            <span className="truncate">{currentWorkspace?.name ?? 'Select workspace'}</span>
            <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0 text-muted-foreground" />
          </button>

          {workspaceSwitcherOpen && workspaces.length > 0 && (
            <div
              className="absolute left-0 top-full z-50 mt-1 w-full rounded-md border bg-popover shadow-md"
              role="listbox"
            >
              {workspaces.map((ws) => (
                <button
                  key={ws.id}
                  type="button"
                  role="option"
                  aria-selected={ws.id === currentWorkspace?.id}
                  onClick={() => {
                    switchWorkspace(ws.id)
                    setWorkspaceSwitcherOpen(false)
                  }}
                  className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-accent"
                >
                  <span className="truncate">{ws.name}</span>
                  {ws.id === currentWorkspace?.id && (
                    <Check className="h-4 w-4 text-brand-blue" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Primary navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-3" aria-label="Main navigation">
        <ul className="space-y-0.5" role="list">
          {NAV_LINKS.map(({ to, label, Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-brand-blue/10 text-brand-blue'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  )
                }
              >
                <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom section */}
      <div className="border-t px-3 py-3 space-y-1">
        {/* Subscription tier badge */}
        {currentWorkspace && (
          <div className="flex items-center justify-between px-3 py-1">
            <span className="text-xs text-muted-foreground">Plan</span>
            <TierBadge tier={tier} />
          </div>
        )}

        {/* Notification Bell */}
        <div className="flex items-center gap-2 rounded-md px-3 py-1 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground">
          <NotificationBell />
          <span>Notifications</span>
        </div>

        {/* Billing */}
        <NavLink
          to="/billing"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-brand-blue/10 text-brand-blue'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )
          }
        >
          <CreditCard className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          Billing
        </NavLink>

        {/* Settings */}
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-brand-blue/10 text-brand-blue'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )
          }
        >
          <Settings className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          Settings
        </NavLink>

        {/* User / sign-out */}
        <div className="flex items-center gap-3 rounded-md px-3 py-2">
          {/* Avatar */}
          <div
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-purple/20"
            aria-hidden="true"
          >
            {user?.imageUrl ? (
              <img
                src={user.imageUrl}
                alt={user.fullName ?? 'User avatar'}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-xs font-semibold text-brand-purple">
                {(user?.firstName?.[0] ?? user?.emailAddresses?.[0]?.emailAddress?.[0] ?? 'U').toUpperCase()}
              </span>
            )}
          </div>

          {/* Name */}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-foreground">
              {user?.fullName ?? user?.emailAddresses?.[0]?.emailAddress ?? 'User'}
            </p>
          </div>

          {/* Sign out */}
          <button
            type="button"
            onClick={handleSignOut}
            title="Sign out"
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  )
}
